import { Injectable, signal } from '@angular/core';

/** Los dos temas disponibles. */
export type Theme = 'dark' | 'light';

/** Clave de localStorage; la comparte el script inline de index.html. */
const STORAGE_KEY = 'cleversec-nps-theme';

/**
 * Tema claro / oscuro.
 *
 * El atributo `data-theme` de `<html>` es la unica fuente de verdad del CSS:
 * todos los tokens de `styles.scss` cuelgan de el. El script inline de
 * `index.html` ya lo dejo puesto antes de que Angular arranque, asi que aqui
 * solo se lee para no provocar un parpadeo.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {

  /** Tema activo. Los componentes lo leen para reaccionar (p. ej. graficas). */
  readonly theme = signal<Theme>(this.readInitialTheme());

  constructor() {
    this.apply(this.theme());
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR API PUBLICA                            */
  /* -------------------------------------------------------------------------- */

  public toggle(): void {
    this.setTheme(this.theme() === 'dark' ? 'light' : 'dark');
  }

  public setTheme(theme: Theme): void {
    this.theme.set(theme);
    this.apply(theme);

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* modo privado o storage bloqueado: el tema vive solo en esta sesion */
    }
  }

  /** Lee un token del tema vigente. Lo usan las graficas, que pintan en canvas
   *  y no pueden heredar variables CSS. */
  public token(name: string, fallback = ''): string {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();

    return value || fallback;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR INTERNO                              */
  /* -------------------------------------------------------------------------- */

  private readInitialTheme(): Theme {
    const stored = document.documentElement.getAttribute('data-theme');

    return stored === 'light' ? 'light' : 'dark';
  }

  private apply(theme: Theme): void {
    document.documentElement.setAttribute('data-theme', theme);
  }
}
